import React, { useState } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const FinancialAnalysisPanel = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('12months');
  const [selectedView, setSelectedView] = useState('cost-analysis');

  const costProjectionData = [
    { month: 'Aug 2025', renewal: 8500, replacement: 12000, savings: 3500 },
    { month: 'Sep 2025', renewal: 4200, replacement: 6800, savings: 2600 },
    { month: 'Oct 2025', renewal: 15600, replacement: 24000, savings: 8400 },
    { month: 'Nov 2025', renewal: 3200, replacement: 4500, savings: 1300 },
    { month: 'Dec 2025', renewal: 22400, replacement: 35000, savings: 12600 },
    { month: 'Jan 2026', renewal: 7800, replacement: 11200, savings: 3400 },
    { month: 'Feb 2026', renewal: 18900, replacement: 28500, savings: 9600 },
    { month: 'Mar 2026', renewal: 9600, replacement: 14200, savings: 4600 },
    { month: 'Apr 2026', renewal: 5400, replacement: 7800, savings: 2400 },
    { month: 'May 2026', renewal: 14200, replacement: 21000, savings: 6800 },
    { month: 'Jun 2026', renewal: 19800, replacement: 29500, savings: 9700 },
    { month: 'Jul 2026', renewal: 11300, replacement: 16800, savings: 5500 }
  ];

  const vendorCostData = [
    { vendor: 'Dell Technologies', cost: 45600, percentage: 28.5, color: '#2563EB' },
    { vendor: 'HP Inc.', cost: 32400, percentage: 20.2, color: '#059669' },
    { vendor: 'Lenovo', cost: 28900, percentage: 18.1, color: '#D97706' },
    { vendor: 'Apple Inc.', cost: 24700, percentage: 15.4, color: '#DC2626' },
    { vendor: 'Microsoft', cost: 18200, percentage: 11.4, color: '#7C3AED' },
    { vendor: 'Others', cost: 10200, percentage: 6.4, color: '#64748B' }
  ];

  const budgetImpactData = [
    { category: 'IT Hardware', allocated: 180000, projected: 165000, variance: -15000 },
    { category: 'Software Licenses', allocated: 95000, projected: 102000, variance: 7000 },
    { category: 'Maintenance', allocated: 45000, projected: 38000, variance: -7000 },
    { category: 'Support Services', allocated: 32000, projected: 35000, variance: 3000 }
  ];

  const roiAnalysisData = [
    { quarter: 'Q3 2025', renewalCost: 28500, replacementCost: 45000, savings: 16500, roi: 57.9 },
    { quarter: 'Q4 2025', renewalCost: 41200, replacementCost: 63500, savings: 22300, roi: 54.1 },
    { quarter: 'Q1 2026', renewalCost: 36300, replacementCost: 54700, savings: 18400, roi: 50.7 },
    { quarter: 'Q2 2026', renewalCost: 39100, replacementCost: 58200, savings: 19100, roi: 48.8 }
  ];

  const periodOptions = [
    { value: '6months', label: 'Next 6 Months' },
    { value: '12months', label: 'Next 12 Months' },
    { value: '24months', label: 'Next 24 Months' }
  ];

  const viewOptions = [
    { value: 'cost-analysis', label: 'Cost Analysis' },
    { value: 'vendor-breakdown', label: 'Vendor Breakdown' },
    { value: 'budget-impact', label: 'Budget Impact' },
    { value: 'roi-analysis', label: 'ROI Analysis' }
  ];

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      const crore = value / 10000000;
      return `₹${crore % 1 === 0 ? crore : crore?.toFixed(2)} Cr`;
    } else if (value >= 100000) {
      const lakh = value / 100000;
      return `₹${lakh % 1 === 0 ? lakh : lakh?.toFixed(2)} L`;
    } else if (value >= 1000) {
      const thousand = value / 1000;
      return `₹${thousand % 1 === 0 ? thousand : thousand?.toFixed(2)} K`;
    }
    return `₹${value?.toLocaleString('en-IN')}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-elevated p-3">
          <p className="font-medium text-popover-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4 text-sm">
              <span style={{ color: entry?.color }}>{entry?.name}:</span>
              <span className="font-medium">{formatCurrency(entry?.value)}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const totalRenewalCost = costProjectionData?.reduce((sum, item) => sum + item?.renewal, 0);
  const totalReplacementCost = costProjectionData?.reduce((sum, item) => sum + item?.replacement, 0);
  const totalSavings = totalReplacementCost - totalRenewalCost;
  const savingsPercentage = ((totalSavings / totalReplacementCost) * 100)?.toFixed(1);

  const renderCostAnalysis = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="TrendingDown" size={20} className="text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Renewal Cost</p>
              <p className="text-xl font-semibold text-card-foreground">{formatCurrency(totalRenewalCost)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-error" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Replacement Cost</p>
              <p className="text-xl font-semibold text-card-foreground">{formatCurrency(totalReplacementCost)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={20} className="text-accent" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Potential Savings</p>
              <p className="text-xl font-semibold text-card-foreground">{formatCurrency(totalSavings)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Percent" size={20} className="text-warning" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Savings Rate</p>
              <p className="text-xl font-semibold text-card-foreground">{savingsPercentage}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cost Projection Chart */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-lg font-semibold text-card-foreground mb-4">Cost Projection Analysis</h4>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={costProjectionData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} tickFormatter={formatCurrency} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="renewal" fill="#059669" name="Renewal Cost" radius={[2, 2, 0, 0]} />
              <Bar dataKey="replacement" fill="#DC2626" name="Replacement Cost" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  const renderVendorBreakdown = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vendor Cost Distribution */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="text-lg font-semibold text-card-foreground mb-4">Vendor Cost Distribution</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={vendorCostData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="cost"
                  label={({ percentage }) => `${percentage}%`}
                >
                  {vendorCostData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Vendor Details */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="text-lg font-semibold text-card-foreground mb-4">Vendor Cost Details</h4>
          <div className="space-y-3">
            {vendorCostData?.map((vendor, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: vendor?.color }}
                  />
                  <span className="font-medium text-card-foreground">{vendor?.vendor}</span>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-card-foreground">{formatCurrency(vendor?.cost)}</p>
                  <p className="text-sm text-muted-foreground">{vendor?.percentage}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderBudgetImpact = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-lg font-semibold text-card-foreground mb-4">Budget Impact Analysis</h4>
        <div className="space-y-4">
          {budgetImpactData?.map((item, index) => (
            <div key={index} className="p-4 border border-border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium text-card-foreground">{item?.category}</h5>
                <span className={`text-sm font-medium ${
                  item?.variance >= 0 ? 'text-error' : 'text-success'
                }`}>
                  {item?.variance >= 0 ? '+' : ''}{formatCurrency(item?.variance)}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Allocated</p>
                  <p className="font-medium">{formatCurrency(item?.allocated)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Projected</p>
                  <p className="font-medium">{formatCurrency(item?.projected)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Variance</p>
                  <p className={`font-medium ${
                    item?.variance >= 0 ? 'text-error' : 'text-success'
                  }`}>
                    {((item?.variance / item?.allocated) * 100)?.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderROIAnalysis = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-lg font-semibold text-card-foreground mb-4">ROI Analysis</h4>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={roiAnalysisData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="quarter" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip 
                formatter={(value, name) => [
                  name === 'roi' ? `${value}%` : formatCurrency(value),
                  name === 'roi' ? 'ROI' : name
                ]}
              />
              <Line 
                type="monotone" 
                dataKey="roi" 
                stroke="#2563EB" 
                strokeWidth={3}
                dot={{ fill: '#2563EB', strokeWidth: 2, r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {roiAnalysisData?.map((quarter, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-4">
            <h5 className="font-medium text-card-foreground mb-2">{quarter?.quarter}</h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Renewal:</span>
                <span>{formatCurrency(quarter?.renewalCost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Replacement:</span>
                <span>{formatCurrency(quarter?.replacementCost)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span className="text-success">Savings:</span>
                <span className="text-success">{formatCurrency(quarter?.savings)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span className="text-accent">ROI:</span>
                <span className="text-accent">{quarter?.roi}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCurrentView = () => {
    switch (selectedView) {
      case 'cost-analysis':
        return renderCostAnalysis();
      case 'vendor-breakdown':
        return renderVendorBreakdown();
      case 'budget-impact':
        return renderBudgetImpact();
      case 'roi-analysis':
        return renderROIAnalysis();
      default:
        return renderCostAnalysis();
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div className="flex items-center space-x-3 mb-4 lg:mb-0">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="DollarSign" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Financial Analysis</h3>
            <p className="text-sm text-muted-foreground">Warranty cost projections and budget impact</p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <Select
            options={viewOptions}
            value={selectedView}
            onChange={setSelectedView}
            className="sm:w-48"
          />
          <Select
            options={periodOptions}
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            className="sm:w-48"
          />
          <Button variant="outline" iconName="Download" iconPosition="left">
            Export Report
          </Button>
        </div>
      </div>

      {renderCurrentView()}
    </div>
  );
};

export default FinancialAnalysisPanel;